/**
 * useLogin Composable
 * 
 * A composable that handles user authentication functionality including login and logout.
 * It manages the login form state, handles form submission, and provides error handling.
 * 
 * Features:
 * - Form state management (email, password)
 * - Login form submission with validation
 * - Error handling and loading states
 * - Logout functionality
 * - Automatic redirection after successful login
 * 
 * @returns {Object} An object containing form state, loading state, error state, and auth methods
 */

import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToastService } from '@/services/toast'

export function useLogin() {
  const authStore = useAuthStore()
  const toast = useToastService()

  const email = ref('')
  const password = ref('')
  const loading = ref(false)
  const error = ref('')

  const handleLogin = async (rememberMe = false) => {
    try {
      loading.value = true
      error.value = ''

      // Validate input
      if (!email.value || !password.value) {
        error.value = 'Please enter both email and password'
        toast.error('Validation Error', 'Please enter both email and password')
        return
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.value)) {
        error.value = 'Please enter a valid email address'
        toast.error('Validation Error', 'Please enter a valid email address')
        return
      }

      // Call auth store login
      await authStore.login({
        email: email.value,
        password: password.value,
        remember_me: rememberMe
      })

      // Clear form on successful login
      email.value = ''
      password.value = ''
    } catch (err: any) {
      console.error('Login error in composable:', err)
      error.value = err.message || 'An error occurred during login'
    } finally {
      loading.value = false
    }
  }

  const handleLogout = async () => {
    try {
      await authStore.logout()
    } catch (error) {
      console.error('Logout failed:', error)
      toast.error('Logout Error', 'Failed to logout properly')
    }
  }

  return {
    email,
    password,
    loading,
    error,
    handleLogin,
    handleLogout
  }
} 